import {ReactNode, useEffect, useState} from 'react';
import {useInView} from '@/hooks/useInView';

interface AnimatedComponentProps {
    children: ReactNode;
    variants?: any;
    className?: string;
    style?: React.CSSProperties;
    tag?: any;
    once?: boolean;
    threshold?: number;
    delay?: number;
    duration?: number;
}

export const AnimatedComponent = ({
    children,
    variants,
    className,
    style,
    tag = 'div',
    once = true,
    threshold = 0.1,
    delay = 0,
    duration
}: AnimatedComponentProps) => {
    const [ref, inView] = useInView({
        triggerOnce: once,
        threshold,
        rootMargin: '50px' // Load slightly before entering viewport
    });

    const [isMounted, setIsMounted] = useState(false);
    const [Framer, setFramer] = useState<{m: any; LazyMotion: any; domAnimation: any} | null>(null);

    useEffect(() => {
        setIsMounted(true);
    }, []);

    useEffect(() => {
        if (!variants) return;

        // Only fetch framer-motion if the component is near the viewport
        if (inView || Framer) {
            import('./LazyFramerMotion').then((mod) => {
                setFramer(mod.getFramerMotion());
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- Framer is a guard ("already loaded?"), not a semantic dependency; adding it would re-trigger the dynamic import on every load
    }, [tag, variants, inView]);

    const FinalTag = tag as keyof JSX.IntrinsicElements | React.ElementType;

    if (!variants) {
        // Fallback: render a plain HTML tag if no animations are configured, saving JS bytes
        return (
            <FinalTag ref={ref} className={className} style={style}>
                {children}
            </FinalTag>
        );
    }

    let finalVariants = variants;
    if (duration && variants) {
        finalVariants = {
            ...variants,
            visible: {
                ...variants.visible,
                transition: {
                    ...variants.visible?.transition,
                    duration
                }
            }
        };
    }

    if (!Framer) {
        // Fallback: render the original element.
        // On server (isMounted=false), we use opacity: 1 for SEO.
        // On client (isMounted=true) before Framer loads, we use opacity: 0 to prevent flashes.
        return (
            <FinalTag ref={ref} className={className} style={{...style, opacity: isMounted ? 0 : 1}}>
                {children}
            </FinalTag>
        );
    }

    const {m, LazyMotion, domAnimation} = Framer;
    const MotionComponent = (m as any)?.[tag] || m.div;

    return (
        <LazyMotion features={domAnimation} strict>
            <MotionComponent
                ref={ref}
                initial="hidden"
                animate={inView ? 'visible' : 'hidden'}
                variants={finalVariants}
                className={className}
                style={style}
                transition={{delay}}
            >
                {children}
            </MotionComponent>
        </LazyMotion>
    );
};
